import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";

// Fixed assets (Equipment, Computer, Printer, Mobile, etc.) live here — separate from the
// Item Master, which is for batch/lot-tracked consumables (Reagent, Consumable, Control,
// Calibrator, Cleaning & Disinfection). Assets are tracked one-by-one by serial number and
// current Location instead, with no stock quantity or expiry to manage.
const fieldGroupOf = (categories, name) => categories.find((c) => c.name === name)?.field_group || "generic";

// IMPORTANT: this must stay a top-level component, not one defined inside AssetRegister().
// A component defined inside another component's function body gets a brand-new identity
// every time the parent re-renders — which happens on every keystroke here, since typing
// calls setForm/setEditForm. React then unmounts the "old" AssetFields and mounts a "new"
// one instead of just updating it, so every input loses focus mid-type and the browser
// jumps the page back to the first focusable field. That was the "auto-scrolls to the top
// while typing" bug in Make/Model/Serial No./Asset Tag — hoisting this out fixes it.
const AssetFields = ({ f, setF, locations, calibrated, testidPrefix }) => (
  <div className="grid grid-cols-2 gap-4">
    <div><Label>Make</Label><Input data-testid={`${testidPrefix}-make`} value={f.manufacturer} onChange={(e) => setF({ ...f, manufacturer: e.target.value })} placeholder={calibrated ? "e.g. Mindray, Roche" : "e.g. Dell, HP, Canon"} /></div>
    <div><Label>Model</Label><Input data-testid={`${testidPrefix}-model`} value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} /></div>
    <div><Label>Serial Number <span className="text-red-500">*</span></Label><Input data-testid={`${testidPrefix}-serial-number`} value={f.serial_number} onChange={(e) => setF({ ...f, serial_number: e.target.value })} /></div>
    <div><Label>Asset Tag</Label><Input data-testid={`${testidPrefix}-asset-tag`} value={f.asset_tag} onChange={(e) => setF({ ...f, asset_tag: e.target.value })} /></div>
    <div>
      <Label>Location <span className="text-red-500">*</span></Label>
      <Select value={f.location_id} onValueChange={(v) => setF({ ...f, location_id: v })}>
        <SelectTrigger data-testid={`${testidPrefix}-location`}><SelectValue placeholder="Where is this asset?" /></SelectTrigger>
        <SelectContent className="bg-white">{locations.map((l) => <SelectItem key={l._id} value={l._id}>{l.name}</SelectItem>)}</SelectContent>
      </Select>
    </div>
    <div><Label>Date of Purchase</Label><Input type="date" value={f.purchase_date || ""} onChange={(e) => setF({ ...f, purchase_date: e.target.value })} /></div>
    {calibrated && (
      <>
        <div><Label>Last Calibration Date</Label><Input type="date" data-testid={`${testidPrefix}-last-calibration`} value={f.last_calibration_date || ""} onChange={(e) => setF({ ...f, last_calibration_date: e.target.value })} /></div>
        <div><Label>Calibration Due Date</Label><Input type="date" data-testid={`${testidPrefix}-calibration-due`} value={f.calibration_due_date || ""} onChange={(e) => setF({ ...f, calibration_due_date: e.target.value })} /></div>
      </>
    )}
    <div><Label>AMC / Warranty Expiry</Label><Input type="date" value={f.amc_expiry_date || ""} onChange={(e) => setF({ ...f, amc_expiry_date: e.target.value })} /></div>
    <div className="col-span-2"><Label>Maintenance Vendor</Label><Input value={f.maintenance_vendor} onChange={(e) => setF({ ...f, maintenance_vendor: e.target.value })} /></div>
  </div>
);

export default function AssetRegister() {
  const qc = useQueryClient();
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: () => api.get("/items?kind=asset").then((r) => r.data) });
  const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: () => api.get("/locations").then((r) => r.data) });
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api.get("/categories").then((r) => r.data) });
  const assetCategories = categories.filter((c) => ["equipment_calibrated", "equipment_asset"].includes(c.field_group));

  const [open, setOpen] = useState(false);
  const blankAsset = {
    name: "", category: "", manufacturer: "", model: "", serial_number: "", asset_tag: "",
    location_id: "", purchase_date: "", last_calibration_date: "", calibration_due_date: "",
    amc_expiry_date: "", maintenance_vendor: "",
  };
  const [form, setForm] = useState(blankAsset);
  const isCalibrated = fieldGroupOf(categories, form.category) === "equipment_calibrated";

  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState(blankAsset);
  const editIsCalibrated = fieldGroupOf(categories, editForm.category) === "equipment_calibrated";
  const [deleteTarget, setDeleteTarget] = useState(null);

  const create = async () => {
    if (!form.name.trim() || !form.category) { toast.error("Name and category are required"); return; }
    if (!form.serial_number.trim()) { toast.error("Serial number is required — this is how the asset is individually tracked"); return; }
    if (!form.location_id) { toast.error("Location is required — so this asset can be found later"); return; }
    try {
      await api.post("/items", { ...form, location_id: form.location_id || null });
      toast.success("Asset added to register");
      setOpen(false); setForm(blankAsset);
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openEdit = (a) => {
    setEditTarget(a);
    setEditForm({ ...blankAsset, ...a, location_id: a.location_id || "" });
  };

  const saveEdit = async () => {
    if (!editTarget) return;
    if (!editForm.name.trim() || !editForm.category) { toast.error("Name and category are required"); return; }
    if (!editForm.serial_number.trim()) { toast.error("Serial number is required — this is how the asset is individually tracked"); return; }
    if (!editForm.location_id) { toast.error("Location is required — so this asset can be found later"); return; }
    try {
      await api.put(`/items/${editTarget._id}`, { ...editForm, location_id: editForm.location_id || null });
      toast.success("Asset updated");
      setEditTarget(null);
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/items/${deleteTarget._id}`);
      toast.success("Asset permanently deleted");
      qc.invalidateQueries({ queryKey: ["assets"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-5" data-testid="assets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-3xl tracking-tight text-navy">Asset Register</h2>
          <p className="text-slate-500 text-sm mt-1">Fixed assets — lab equipment, computers, printers, mobiles — tracked individually by serial number &amp; location.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="open-add-asset-button" style={{ background: "#042C53" }} className="text-white">
              <Plus size={16} className="mr-2" /> Add Asset
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-serif text-navy text-xl">New Asset</DialogTitle></DialogHeader>

            <div className="pb-4 mb-1 border-b border-slate-200">
              <Label className="text-navy font-semibold">1. Category</Label>
              <p className="text-xs text-slate-500 mb-2">Lab equipment gets calibration tracking; office/IT assets don't.</p>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="asset-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {assetCategories.filter((c) => c.active).map((c) => <SelectItem key={c._id} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="mb-4"><Label>Name</Label><Input data-testid="asset-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Hematology Analyzer, Reception PC" /></div>

            <AssetFields f={form} setF={setForm} locations={locations} calibrated={isCalibrated} testidPrefix="asset" />

            <DialogFooter>
              <Button onClick={create} data-testid="asset-submit" style={{ background: "#042C53" }} className="text-white">Save Asset</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-white border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Category</th>
                <th className="px-4 py-3 font-semibold">Make / Model</th>
                <th className="px-4 py-3 font-semibold">Serial No.</th>
                <th className="px-4 py-3 font-semibold">Location</th>
                <th className="px-4 py-3 font-semibold">Calibration Due</th>
                <th className="px-4 py-3 font-semibold">AMC / Warranty</th>
                <th className="px-4 py-3 font-semibold text-right">Edit</th>
                <th className="px-4 py-3 font-semibold text-right">Delete</th>
              </tr>
            </thead>
            <tbody>
              {assets.map((a, idx) => (
                <tr key={a._id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors duration-200" data-testid={`asset-row-${idx}`}>
                  <td className="px-4 py-3 font-medium text-navy">{a.name}</td>
                  <td className="px-4 py-3 text-slate-600">{a.category}</td>
                  <td className="px-4 py-3 text-slate-600">{[a.manufacturer, a.model].filter(Boolean).join(" ") || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{a.serial_number || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={13} className="text-slate-400" /> {a.location_name || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{a.calibration_due_date || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{a.amc_expiry_date || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => openEdit(a)} data-testid={`asset-edit-${idx}`}
                      className="text-slate-400 hover:text-teal hover:bg-slate-100"
                    >
                      <Pencil size={16} />
                    </Button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost" size="icon"
                      onClick={() => setDeleteTarget(a)} data-testid={`asset-delete-${idx}`}
                      className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
              {assets.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No assets yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-navy text-xl">Edit {editTarget?.name}</DialogTitle></DialogHeader>

          <div className="pb-4 mb-1 border-b border-slate-200">
            <Label className="text-navy font-semibold">Category</Label>
            <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
              <SelectTrigger data-testid="edit-asset-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent className="bg-white">
                {assetCategories.filter((c) => c.active || c.name === editForm.category).map((c) => <SelectItem key={c._id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="mb-4"><Label>Name</Label><Input data-testid="edit-asset-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>

          <p className="text-xs text-slate-500 -mt-2 mb-3">Update Location whenever this asset is physically moved, so it can always be found.</p>
          <AssetFields f={editForm} setF={setEditForm} locations={locations} calibrated={editIsCalibrated} testidPrefix="edit-asset" />

          <DialogFooter>
            <Button onClick={saveEdit} data-testid="edit-asset-submit" style={{ background: "#042C53" }} className="text-white">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {deleteTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the asset from the register entirely — it cannot be undone.
              Only allowed when it has no linked stock/transaction history. If this asset
              was entered by mistake and has no history yet, it's safe to delete.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="asset-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete} data-testid="asset-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
